import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Library } from 'lucide-react';
import { loginWithGoogle } from '../api/auth';
import { useAuthStore } from '../store/authStore';
import { requestGoogleAuthCode } from '../lib/googleIdentity';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

export default function LoginPage() {
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const setAuth = useAuthStore((state) => state.setAuth);

  const from = (location.state as { from?: string })?.from || '/';

  const handleGoogleSignIn = async () => {
    if (!GOOGLE_CLIENT_ID) {
      setError('Google sign-in is not configured');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const code = await requestGoogleAuthCode(GOOGLE_CLIENT_ID);
      const response = await loginWithGoogle(code, rememberMe);
      setAuth(
        {
          id: response.user.id,
          username: response.user.username,
          displayName: response.user.display_name,
          isAdmin: response.user.is_admin,
        },
        response.access_token,
        response.expires_at
      );
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <Library className="login-icon" />
          <h1>Library</h1>
        </div>

        {error && <div className="login-error">{error}</div>}

        <div className="login-form">
          <div className="form-group checkbox">
            <label>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              <span>Stay signed in</span>
            </label>
          </div>

          <button
            type="button"
            className="btn btn-primary login-google-btn"
            disabled={loading}
            onClick={() => void handleGoogleSignIn()}
          >
            {loading ? 'Signing in...' : 'Sign in with Google'}
          </button>
        </div>
      </div>
    </div>
  );
}
